import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoDialog, FoKpis, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, SelectField, TextField, VendorSelect, useOsResource, useVendorOptions, vendorName } from "./osForm";
import { BILLING_CADENCE, MEMBERSHIP_STATUS, pageMeta } from "./osRules";

const blank = { vendorId: "", product: "Fulfillment", plan: "", mrr: "", billingCadence: "Monthly", status: "Active", renewalDate: "" };

export default function Memberships() {
  const vendors = useVendorOptions();
  const [vendorId, setVendorId] = useState("");
  const [status, setStatus] = useState("");
  const [applied, setApplied] = useState({ vendorId: "", status: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blank);
  const [saving, setSaving] = useState(false);

  const summary = useOsResource((auth) => os.membershipSummary(auth), []);
  const list = useOsResource(
    (auth) => os.memberships(auth, { vendorId: applied.vendorId, status: applied.status, page, limit }),
    [applied.vendorId, applied.status, page, limit]
  );
  const pack = pageMeta(list.data);

  const save = async () => {
    if (!draft.vendorId || !draft.product || !draft.plan || !draft.renewalDate) {
      notification.error({ message: "Brand, product, plan, and renewal date are required." });
      return;
    }
    setSaving(true);
    try {
      await os.createMembership(list.token, {
        ...draft,
        mrr: Number(draft.mrr) || 0,
      });
      notification.success({ message: "Membership saved." });
      setOpen(false);
      setDraft(blank);
      list.reload();
      summary.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fo-stack">
      <FoKpis
        items={[
          { label: "Active MRR", value: `${formatEgp(summary.data?.activeMrr)} EGP`, note: "Trial plans are excluded" },
          { label: "Active memberships", value: summary.data?.activeCount || 0 },
        ]}
      />
      <div className="fo-tools">
        <VendorSelect vendors={vendors} value={vendorId} onChange={setVendorId} emptyLabel="All brands" />
        <SelectField label="Status" value={status} onChange={setStatus} options={MEMBERSHIP_STATUS} allowEmpty />
        <button type="button" className="fo-btn" onClick={() => { setPage(1); setApplied({ vendorId, status }); }}>Apply</button>
        <button type="button" className="fo-btn fo-btn-primary" onClick={() => setOpen(true)}>Add membership</button>
      </div>
      <FoCard title="Products and memberships" note="Active MRR is the sum of Active plans. Trial rows stay on the list and out of that total.">
        <PagerTable
          loading={list.loading}
          dataSource={pack.docs}
          rowKey="_id"
          page={page}
          limit={limit}
          total={pack.total}
          onChange={(nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }}
          empty="No memberships yet."
          columns={[
            { title: "Brand", dataIndex: "vendor", render: (vendor) => vendorName(vendor) },
            { title: "Product", dataIndex: "product" },
            { title: "Plan", dataIndex: "plan" },
            { title: "MRR", dataIndex: "mrr", render: (value) => `${formatEgp(value)} EGP` },
            { title: "Cadence", dataIndex: "billingCadence" },
            { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
            { title: "Renewal", dataIndex: "renewalDate" },
          ]}
        />
      </FoCard>
      <FoDialog open={open} title="Add membership" onClose={() => !saving && setOpen(false)}>
        <div className="fo-form-grid">
          <VendorSelect vendors={vendors} value={draft.vendorId} onChange={(value) => setDraft({ ...draft, vendorId: value })} allowEmpty emptyLabel="Select a brand" />
          <TextField label="Product" value={draft.product} onChange={(product) => setDraft({ ...draft, product })} />
          <TextField label="Plan" value={draft.plan} onChange={(plan) => setDraft({ ...draft, plan })} />
          <TextField label="MRR" type="number" value={draft.mrr} onChange={(mrr) => setDraft({ ...draft, mrr })} />
          <SelectField label="Cadence" value={draft.billingCadence} onChange={(billingCadence) => setDraft({ ...draft, billingCadence })} options={BILLING_CADENCE} />
          <SelectField label="Status" value={draft.status} onChange={(next) => setDraft({ ...draft, status: next })} options={MEMBERSHIP_STATUS} />
          <TextField label="Renewal" type="date" value={draft.renewalDate} onChange={(renewalDate) => setDraft({ ...draft, renewalDate })} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={saving} onClick={save}>Save</button>
        </div>
      </FoDialog>
    </div>
  );
}
